import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { useEdiTokenObtain, useEdiTokenObtainMfa } from '@/api/hanly-v2/edi/edi';
import type { EdiTokenObtainResponse } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { PasswordInput } from '@/components/ui/password-input';
import { getAnimalIdConfig, startAnimalIdSignIn } from '@/features/auth/animal-id';
import { MfaSignInForm } from '@/features/auth/mfa-sign-in-form';
import {
  ANIMAL_ID_ERROR_MESSAGE,
  parseMfaSignInError,
  parseSignInError,
} from '@/features/auth/sign-in-error';
import { saveTokens } from '@/libs/auth-storage';
import { sanitizeReturnTo } from '@/utils/return-to';
import { emailSchema } from '@/utils/schemas';

// ログインでは作成時のパスワード要件は問わない。要件を満たさない既存パスワードでもログインできるようにする
const signinSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'パスワードを入力してください'),
});

type SignInFormData = z.infer<typeof signinSchema>;

const features = [
  'スマートフォンからも発注できます',
  '過去の注文からそのまま再注文できます',
  '注文の確定状況をいつでも確認できます',
];

/** ログイン後の移動先が発注詳細なら、何のページかを添えて案内する */
const describeReturnTo = (returnTo: string) => {
  const orderId = returnTo.match(/^\/orders\/(\d+)/)?.[1];
  return orderId ? `メールで開いたページ（発注 EDI No. ${orderId}）` : '開こうとしていたページ';
};

const LoginHelp = () => (
  <section className="flex flex-col gap-3 border-t border-neutral-200 pt-5">
    <h3 className="text-base">ログインできない場合</h3>
    <div>
      <p className="font-bold">パスワードを忘れた</p>
      <p className="text-neutral-600">
        <Link to="/password/reset" className="text-sky-800 underline hover:opacity-60">
          パスワードの再設定
        </Link>
        から、ご登録のメールアドレス宛に再設定用のリンクをお送りします。
      </p>
    </div>
    <div>
      <p className="font-bold">アカウントをお持ちでない</p>
      <p className="text-neutral-600">
        発注先から届く招待メールのリンクからアカウントを作成できます。届いていない場合は発注先のご担当者にお問い合わせください。
      </p>
    </div>
  </section>
);

export const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const animalId = getAnimalIdConfig();
  // 未ログインで踏んだリンク等の戻り先。外部URLを渡されても遷移しないようここで検証する
  const returnTo = sanitizeReturnTo(searchParams.get('next'));
  const [apiError, setApiError] = useState('');
  // MFA が有効なユーザーはパスワードの後にコードの入力を求められる。コード入力中はその短命トークンを持つ
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | undefined>(undefined);
  // コード入力中に短命トークンが期限切れになり、パスワードの入力に戻した(パスワード誤りとは別に案内する)
  const [mfaExpired, setMfaExpired] = useState(false);
  // アニマルID でのログインに失敗した理由。戻り先のページが state で渡す
  const [animalIdError, setAnimalIdError] = useState<string>(
    () => (location.state as { signInError?: string } | null)?.signInError ?? '',
  );

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signinSchema),
  });

  const { mutate: tokenObtain, isPending } = useEdiTokenObtain();
  const { mutate: tokenObtainMfa, isPending: isMfaPending } = useEdiTokenObtainMfa();

  const completeSignIn = (response: EdiTokenObtainResponse) => {
    saveTokens({ access: response.access, refresh: response.refresh });
    navigate(returnTo ?? '/orders', { replace: true });
  };

  const onSubmit = (data: SignInFormData) => {
    setApiError('');
    setMfaExpired(false);
    setAnimalIdError('');
    tokenObtain(
      { data: { email: data.email, password: data.password } },
      {
        onSuccess: completeSignIn,
        onError: async (error) => {
          const failure = await parseSignInError(error);
          if (failure.type === 'mfa_required') {
            setMfaError(undefined);
            setMfaToken(failure.mfaToken);
            return;
          }
          setApiError(failure.message);
        },
      },
    );
  };

  const signInWithAnimalId = async () => {
    if (!animalId) return;
    setAnimalIdError('');
    try {
      window.location.assign(await startAnimalIdSignIn(animalId, returnTo));
    } catch {
      // Web Crypto を使えない(https でない)ページなど
      setAnimalIdError(ANIMAL_ID_ERROR_MESSAGE);
    }
  };

  const onSubmitMfa = (token: string, mfaCode: string) => {
    setMfaError(undefined);
    tokenObtainMfa(
      { data: { mfa_token: token, mfa_code: mfaCode } },
      {
        onSuccess: completeSignIn,
        onError: async (error) => {
          const failure = await parseMfaSignInError(error);
          if (failure.type === 'expired') {
            // 短命トークンの期限切れ。パスワードの入力からやり直してもらう
            setMfaToken(null);
            setMfaExpired(true);
            return;
          }
          setMfaError(failure.message);
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen w-full flex-col md:flex-row">
      <section className="hidden flex-1 flex-col justify-center gap-7 bg-[#00598a] px-14 py-16 text-white md:flex">
        <img src={logo} alt="Animal EDI" className="h-9 self-start brightness-0 invert" />
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl leading-snug font-bold">取引先様専用の発注サイト</h1>
          <p className="text-white/85">発注先が用意した商品カタログから、いつでも発注できます。</p>
        </div>
        <ul className="flex flex-col gap-3">
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-2.5">
              <CheckIcon className="size-5 shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-1 flex-col md:items-center md:justify-center md:px-14 md:py-16">
        <div className="flex flex-col items-center gap-3 border-b border-neutral-200 px-6 pt-14 pb-7 text-center md:hidden">
          <img src={logo} alt="Animal EDI" className="h-8" />
          <p className="font-bold">取引先様専用の発注サイト</p>
          <p className="text-sm text-neutral-600">発注先からご案内したアカウントでログインしてください。</p>
        </div>

        <div className="flex w-full flex-col gap-6 px-6 py-7 md:max-w-md md:px-0 md:py-0">
          {mfaToken ? (
            // コード入力はスマホでも見出しを出し、パスワードの入力から画面が切り替わったことを分かるようにする
            <h2>認証コードの入力</h2>
          ) : (
            <div className="hidden flex-col gap-1 md:flex">
              <h2>ログイン</h2>
              <p className="text-neutral-600">発注先からご案内したアカウントでログインしてください。</p>
            </div>
          )}

          {mfaToken ? (
            <MfaSignInForm
              email={getValues('email')}
              onSubmit={(mfaCode) => onSubmitMfa(mfaToken, mfaCode)}
              onBack={() => setMfaToken(null)}
              isPending={isMfaPending}
              errorMessage={mfaError}
              onCodeTypeChange={() => setMfaError(undefined)}
            />
          ) : (
            <>
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
                {returnTo && (
                  <p className="flex gap-2 text-sm text-neutral-600">
                    <InformationCircleIcon className="mt-0.5 size-5 shrink-0 text-sky-800" />
                    ログイン後、{describeReturnTo(returnTo)}に移動します。
                  </p>
                )}
                {mfaExpired && (
                  <p role="alert" className="flex gap-2 font-bold text-red-700">
                    <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
                    時間切れのため、もう一度ログインしてください。
                  </p>
                )}
                {apiError && (
                  <p role="alert" className="flex gap-2 font-bold text-red-700">
                    <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
                    {apiError}
                  </p>
                )}
                {animalIdError && (
                  <p role="alert" className="flex gap-2 font-bold text-red-700">
                    <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
                    {animalIdError}
                  </p>
                )}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="font-bold">
                    メールアドレス
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="例）tanaka@example.co.jp"
                    aria-invalid={errors.email ? true : undefined}
                    className="h-12 text-base! md:h-11"
                    {...register('email')}
                  />
                  <FormError message={errors.email?.message} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="password" className="font-bold">
                    パスワード
                  </label>
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    invalid={!!apiError || !!errors.password}
                    className="h-12 text-base md:h-11"
                    {...register('password')}
                  />
                  <FormError message={errors.password?.message} />
                  {apiError && <p className="text-sm text-neutral-600">大文字・小文字も区別されます。</p>}
                  <Link
                    to="/password/reset"
                    className="self-end text-sm text-neutral-600 underline hover:text-neutral-900"
                  >
                    パスワードを忘れた場合
                  </Link>
                </div>
                <Button type="submit" size="lg" fullWidth loading={isPending} className="mt-1 text-base">
                  ログイン
                </Button>
              </form>

              {animalId && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3 text-sm text-neutral-500">
                    <span aria-hidden className="h-px flex-1 bg-neutral-200" />
                    または
                    <span aria-hidden className="h-px flex-1 bg-neutral-200" />
                  </div>
                  <Button
                    type="button"
                    variant="outlined"
                    size="lg"
                    fullWidth
                    className="text-base"
                    onClick={signInWithAnimalId}
                  >
                    アニマルID でログイン
                  </Button>
                </div>
              )}

              <LoginHelp />
            </>
          )}
        </div>
      </section>
    </div>
  );
};
